import React from 'react';
import { injectIntl } from 'react-intl';
import { useFormContext, Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { Form, Label } from 'semantic-ui-react';
import StoredAccordion from 'src/components/StoredAccordion';
import {
  getAllEquipmentFromChoices,
  isChoicesComplex,
  complexControlGetters,
  configSupportsComplexGetters,
  pathNames8XX,
  carrierNames450v,
} from './utils';
import { useLinkKind } from './hooks';
import { linkKindsMultiband2plus0CrossPol } from './link-kind-utils';
import './PTPEquipmentPanel.css';
import { getDisplayValue } from 'src/utils/useful_functions';
import get from 'lodash/get';

const getterRe = /(\w+)\.radios\.0\.equipment\.(\w+)/;

function EquipmentControl(props) {
  const {
    field,
    getter,
    level,
    choices,
    setModified,
    refreshChoices,
    pathIdx,
  } = props;
  const { control, setValue, getValues } = useFormContext();
  const { kind: lkKind } = useLinkKind();
  const permissionWrite = useSelector(
    (state: any) => state.mainFrame.permissionWrite
  );

  const disabled = field.editable === false; // ignore undefined
  return (
    <Controller
      control={control}
      name={getter}
      render={({ field: { ref, onChange, ...rest } }) => (
        <Form.Select
          options={level.choices}
          {...rest}
          disabled={!permissionWrite || disabled}
          onChange={(e, data) => {
            onChange(data.value);

            // sync equipment to other paths if required
            // NOTE: pathIdx is only defined for complex links
            if (pathIdx != null) {
              // we get here if the list of getters > 1,
              // e.g. cross polar polarization
              // due to checks in complexControlGetters, we know that the structure
              // "field.lk[lkKind]" is not null
              if (field.lk[lkKind].sync != null) {
                // this will appropriately sync other paths, e.g. set the reverse
                // polarization on the other path(s)
                field.lk[lkKind].sync(getValues, setValue, pathIdx, data.value);
              }
            } else if (isChoicesComplex(choices)) {
              // we get here if its complex but the attr is synced across
              // all paths, e.g. 4+0 co polar polarization
              if (getterRe.test(getter)) {
                // TODO following line is broken (pathIdx is always null)
                const otherIdx = pathIdx === 1 ? 0 : 1;
                const sync = field.getter.replace(
                  getterRe,
                  `$1.radios.${otherIdx}.equipment.$2`
                );
                setValue(sync, data.value, { shouldDirty: true });
              }
            }

            if (field.afterOnChange != null) {
              field.afterOnChange(data.value, { getValues, setValue });
            }

            setModified(true);
            refreshChoices({ field });
          }}
        />
      )}
    />
  );
}

function isValidField(choices, attrName) {
  if (choices == null || choices.length === 0) {
    return false;
  }

  function isValidForChoice(choice) {
    if (choice == null) {
      return false;
    }

    return (
      choice.hasOwnProperty(attrName) ||
      (choice.sync_params != null && get(choice, attrName) != null)
    );
  }

  if (choices.length === 1) {
    return isValidForChoice(choices[0]);
  } else {
    return choices.some(isValidForChoice);
  }
}

function getFirstDefinedLevel(choices, attrName) {
  const choice = choices.find((item) => get(item, attrName) != null);
  return get(choice, attrName);
}

function PTPEquipmentPanel(props: any) {
  const { control, getValues, setValue } = useFormContext();
  const { lk, kind: lkKind } = useLinkKind();

  const { setModified, panel, path, choices } = props;
  const allEquipmentChoices = getAllEquipmentFromChoices(choices);

  const complexLinkLabels =
    lk === 'carrier_2_cc_2' || lk === 'carrier_2_cc_3'
      ? carrierNames450v
      : pathNames8XX;

  return (
    <StoredAccordion name={`ptp_${panel.name}`} title={panel.title}>
      {panel.fields.map((field, i) => {
        if (!isValidField(allEquipmentChoices, field.attrName)) {
          return null;
        }

        if (
          field.show != null &&
          !field.show({ choices, formGetter: getValues })
        ) {
          return null;
        }

        let message: any = null;
        if (field.info != null) {
          const text = field.info({ path, choices, formGetter: getValues });
          if (text != null) {
            message = (
              <Label
                color={field.infoColor ?? 'yellow'}
                pointing
                style={{ marginTop: 0, width: '100%' }}
              >
                {text}
              </Label>
            );
          }
        }

        let ctrl: any = null;
        let showTopLabel = true;
        let level = null;
        const firstDefinedLevel = getFirstDefinedLevel(
          allEquipmentChoices,
          field.attrName
        );

        // Multiband 2+0 Cross-Polar / XPIC: Link B is a 2+0 whose two carriers
        // (Link B1 / Link B2) hold independent polarizations, so label the
        // polarization column "Link B1" rather than the generic "Link B".
        const fieldLinkLabels =
          field.attrName === 'polarization' &&
          linkKindsMultiband2plus0CrossPol.includes(lk)
            ? ['Link A', 'Link B1', 'Link C', 'Link D']
            : complexLinkLabels;

        const getterList = complexControlGetters(field, lk, lkKind);
        if (
          getterList.length > 1 &&
          configSupportsComplexGetters(field, choices)
        ) {
          showTopLabel = false;
          const levels = allEquipmentChoices.map((c) => get(c, field.attrName));
          ctrl = (
            <div className="splitEquipmentWrapper">
              {getterList.map((getter, i) => {
                // sometimes when changing link kind, we get an intermediate
                // render where the choices haven't caught up with the getters
                // yet - in that case fall back to "1+0 style" choices.
                const pathExists = i < allEquipmentChoices.length;
                const level = pathExists ? levels[i] : firstDefinedLevel;
                if (level == null) {
                  // The path exists but has no choices for this attribute,
                  // e.g. on a multiband link Link B may not support ACMB, or
                  // may use a fixed mod mode so has no max/min mod mode. Render
                  // an empty placeholder so this column is left blank and the
                  // other path(s) stay aligned, rather than showing the other
                  // path's value here.
                  return <span className="field" key={`${getter}-${i}`} />;
                }
                return (
                  <span className="field" key={`${getter}-${i}`}>
                    <label>
                      {field.label ?? level.display_name} (
                      {fieldLinkLabels[i]})
                    </label>
                    {level.choices != null && level.choices.length > 1 ? (
                      <EquipmentControl
                        field={field}
                        getter={getter}
                        level={level}
                        choices={choices}
                        setModified={setModified}
                        refreshChoices={props.refreshChoices}
                        pathIdx={i}
                      />
                    ) : (
                      <p>{getDisplayValue(field, level)}</p>
                    )}
                  </span>
                );
              })}
            </div>
          );
        } else {
          level = firstDefinedLevel;
          let cprops: any = {};
          if (typeof field.componentProps === 'function') {
            cprops = field.componentProps({ choices, getValues });
          } else if (field.componentProps) {
            cprops = field.componentProps;
          }
          if (level == null) {
            return null;
          }
          if (level.choices != null && level.choices.length > 1) {
            ctrl = (
              <EquipmentControl
                field={field}
                getter={getterList[0]}
                level={level}
                choices={choices}
                setModified={setModified}
                refreshChoices={props.refreshChoices}
              />
            );
          } else {
            ctrl = <p {...cprops}>{getDisplayValue(field, level)}</p>;
          }
        }

        return (
          <Form.Field key={field.getter}>
            {showTopLabel ? (
              <label>{field.label ?? level.display_name}</label>
            ) : null}
            {ctrl}
            {message}
          </Form.Field>
        );
      })}
      {panel.panelMessage && (
        <>
          {typeof panel.panelMessage === 'function'
            ? panel.panelMessage({ path, choices, formGetter: getValues })
            : panel.panelMessage}
        </>
      )}
    </StoredAccordion>
  );
}

export default injectIntl(PTPEquipmentPanel);
